import { Box, MenuItem, Stack, TextField } from "@mui/material";
import { useMemo, useState } from "react";

type CountryOption = {
  code: string;
  name: string;
  dialCode: string;
  flag: string;
};

interface AppPhoneInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const countryOptions: CountryOption[] = [
  { code: "BA", name: "Bosnia and Herzegovina", dialCode: "+387", flag: "🇧🇦" },
  { code: "HR", name: "Croatia", dialCode: "+385", flag: "🇭🇷" },
  { code: "RS", name: "Serbia", dialCode: "+381", flag: "🇷🇸" },
  { code: "ME", name: "Montenegro", dialCode: "+382", flag: "🇲🇪" },
  { code: "SI", name: "Slovenia", dialCode: "+386", flag: "🇸🇮" },
  { code: "DE", name: "Germany", dialCode: "+49", flag: "🇩🇪" },
  { code: "AT", name: "Austria", dialCode: "+43", flag: "🇦🇹" },
  { code: "CH", name: "Switzerland", dialCode: "+41", flag: "🇨🇭" },
  { code: "FR", name: "France", dialCode: "+33", flag: "🇫🇷" },
  { code: "ES", name: "Spain", dialCode: "+34", flag: "🇪🇸" },
  { code: "IT", name: "Italy", dialCode: "+39", flag: "🇮🇹" },
  { code: "US", name: "United States", dialCode: "+1", flag: "🇺🇸" },
  { code: "GB", name: "United Kingdom", dialCode: "+44", flag: "🇬🇧" },
];

const onlyDigits = (value: string) => value.replace(/\D/g, "");

const getDetectedCountry = (value: string) => {
  const normalizedValue = value.trim();

  if (!normalizedValue.startsWith("+")) {
    return countryOptions[0];
  }

  return (
    [...countryOptions]
      .sort((a, b) => b.dialCode.length - a.dialCode.length)
      .find((country) => normalizedValue.startsWith(country.dialCode)) ||
    countryOptions[0]
  );
};

const getNationalValue = (value: string, country: CountryOption) => {
  if (!value.trim()) {
    return "";
  }

  if (value.startsWith(country.dialCode)) {
    return onlyDigits(value.slice(country.dialCode.length));
  }

  if (value.startsWith("+")) {
    return onlyDigits(value);
  }

  return onlyDigits(value).replace(/^0+/, "");
};

const AppPhoneInput = ({
  label,
  value,
  onChange,
  placeholder,
  disabled,
}: AppPhoneInputProps) => {
  const initialCountry = useMemo(() => getDetectedCountry(value), [value]);
  const [countryCode, setCountryCode] = useState(initialCountry.code);

  const selectedCountry =
    countryOptions.find((country) => country.code === countryCode) ||
    countryOptions[0];

  const nationalValue = getNationalValue(value, selectedCountry);

  const handleCountryChange = (nextCountryCode: string) => {
    const nextCountry =
      countryOptions.find((country) => country.code === nextCountryCode) ||
      countryOptions[0];

    setCountryCode(nextCountry.code);

    if (nationalValue) {
      onChange(`${nextCountry.dialCode}${nationalValue}`);
    }
  };

  const handleNationalNumberChange = (nextValue: string) => {
    const digits = onlyDigits(nextValue).replace(/^0+/, "");

    onChange(digits ? `${selectedCountry.dialCode}${digits}` : "");
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Stack direction="row" spacing={1}>
        <TextField
          select
          label="Code"
          value={selectedCountry.code}
          disabled={disabled}
          onChange={(event) => handleCountryChange(event.target.value)}
          sx={{ width: 132 }}
        >
          {countryOptions.map((country) => (
            <MenuItem key={country.code} value={country.code}>
              {country.flag} {country.dialCode}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          type="tel"
          label={label}
          value={nationalValue}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(event) => handleNationalNumberChange(event.target.value)}
        />
      </Stack>
    </Box>
  );
};

export default AppPhoneInput;
